import {Component, OnInit} from '@angular/core';
import {Preferences} from '../../../model/preferences.model';
import {ChartData} from '../../../doughnut/doughnut.component';
import {ChartDataUtil} from '../../../doughnut/chart-data.util';
import {Keys, StorageService} from '../../../service/storage.service';
import {PreferencesService} from '../../../service/preferences.service';
import {InvestmentMetrics, LenderDetailsService} from '../../../service/lender-details.service';
import {PortfolioService} from '../../../service/portfolio.service';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {ApiServiceService} from '../../../api-service.service';
import {MatDialog} from '@angular/material';
import {CreatePortfolioComponent} from '../../../create-portfolio/create-portfolio.component';
import {ConfigService} from '../../../service/config.service';
import {isEmpty, sortBy} from 'lodash';
import {PortfolioUtil} from '../../../utils/PortfolioUtil';
import {PortfolioType} from '../../../model/portfolio-type.model';
import moment = require('moment');
import {UserAttributeInterface} from '../../../model/finzy-flex/user-attribute.model';
import {Toaster, Type} from '../../../toaster/Toaster';
import {ShowTurnOffSwiftPopupUtil} from '../../../utils/finzy-flex/show-turn-off-swift-popup.utils';

@Component({
    selector: 'app-investments',
    templateUrl: './investments.component.html',
    styleUrls: ['./investments.component.css']
})
export class InvestmentsComponent implements OnInit {
    public static minimumNumberOfPortfolioToDisplay = 4;
    minimumInvestmentLimit: number;
    maximumInvestmentLimit: number;
    lender10LakhLimitDeclarationCheck: boolean;
    lenderPortfolios: Array<any>;
    public metrics: InvestmentMetrics;
    walletAmount: number;
    investmentAmt: number;
    denomination: number;
    nonNpaDelayedLoansCount: number;
    overallChartData: ChartData[];
    preferences: Preferences;
    portfolioChartData: { portfolioId: string, chartData: ChartData[] }[];
    insufficientWallet: boolean;
    isMinimumWalletBalanceExistForInitialPortfolio: boolean;
    isShowMoreRequired = false;
    isShowLessRequired = false;
    fewLenderPortfolios: Array<any>;
    createPortfolioAllowed: boolean = true;
    loader: {spin: boolean} = {spin: false};
    
    // check is FINX user
    isFINXLender: boolean = false;

    private userId: string;
    public readonly configService: ConfigService;

    private readonly chartDataUtil: ChartDataUtil;
    private readonly storageService: StorageService;
    private readonly preferenceService: PreferencesService;
    private readonly lenderDetailsService: LenderDetailsService;
    private readonly portfolioService: PortfolioService;
    private readonly showTurnOffSwiftPopup: ShowTurnOffSwiftPopupUtil;

    constructor(private http: HttpClient, private router: Router, private ref: ApiServiceService, public dialog: MatDialog,
                lenderDetailsService: LenderDetailsService, storageService: StorageService,
                preferencesService: PreferencesService, chartDataUtil: ChartDataUtil, portfolioService: PortfolioService, configService: ConfigService, showTurnOffSwiftPopup: ShowTurnOffSwiftPopupUtil) {
        this.storageService = storageService;
        this.lenderDetailsService = lenderDetailsService;
        this.preferenceService = preferencesService;
        this.chartDataUtil = chartDataUtil;
        this.portfolioService = portfolioService;
        this.overallChartData = [];
        this.configService = configService;
        this.showTurnOffSwiftPopup = showTurnOffSwiftPopup;
        this.isFINXLender = this.storageService.getItem(Keys.FINX) === "true" && this.storageService.getItem(Keys.IS_LENDER_CREATED_AFTER_RESTRICTED_DATE) === "true";
    }

    goToPortfolio(id) {
        this.router.navigate(['/portfoliodetail/' + id]);
    }

    async openDialog() {

        // this condition is to restrict FINX user to create portfolio
        if(this.isFINXLender) {
            return;
        }

        if (!this.isPortfolioCreationEnabled()) {
            return;
        }
        if(this.showTurnOffSwiftPopup.isSwiftEnabled(this.preferences)) {
            try {
                const response: UserAttributeInterface = await this.showTurnOffSwiftPopup.showFinzySwiftPopup(this.preferences, this.loader, this.userId);
                this.loader.spin = false;
                if (response.status === 0) {
                    this.preferences.investmentAttrs = response.data[0].investmentPreferences;
                    Toaster.toast('Your swift preference switched off successfully!', Type.Success);
                    this.openCreatePortfolioPopup();
                } 
                else if(response.status === 1){
                    Toaster.toast('Failed to update preference. Please try again after sometime.', Type.Error);
                }
            } catch (error) {
                Toaster.toast('Please try again', Type.Error);
                this.loader.spin = false;
            }
        }
        else {
            this.openCreatePortfolioPopup();
        }
    }

    async ngOnInit() {
        // TODO: Introduce loader;
        const lenderId = this.storageService.getItem(Keys.LENDER_ID);
        const profileId = this.storageService.getItem(Keys.PROFILE_ID);
        this.denomination = ConfigService.getDenomination();
        this.minimumInvestmentLimit = ConfigService.getMinimumInvestmentLimit();
        this.maximumInvestmentLimit = ConfigService.getMaximumInvestmentLimit();
        this.dialog.closeAll();

        await this.getAndShowLenderMetrics();
        this.lenderPortfolios = await this.getLenderPortfolios(lenderId);
        this.populateInvestmentChartData(this.lenderPortfolios);

        let lenderDetails = await this.lenderDetailsService.getLenderWalletAmountAndPhotoUrl(profileId);
        this.updateWalletAmount(lenderDetails);

        this.userId = this.storageService.getItem(Keys.PROFILE_ID);
        this.preferences = await this.preferenceService.getUserPreferences(this.userId);
        this.setShowMoreAndFillFewLenderPortfolios();
        if(this.storageService.getItem(Keys.FINX) == 'true' && this.storageService.getItem(Keys.IS_LENDER_CREATED_AFTER_RESTRICTED_DATE) === "true"){
            this.createPortfolioAllowed = this.configService.flexProFeatureOn();
        }
    }

    populateInvestmentChartData(portfolios: any[]): void {
        this.overallChartData = this.chartDataUtil.convertToChartData(portfolios);
        this.portfolioChartData = portfolios.map(portfolio => ({
            portfolioId: portfolio._id,
            chartData: this.chartDataUtil.convertToChartDataForPortfolio(portfolio)
        }));
    }

    showPieChart(): boolean {
        return this.overallChartData.some(c => c.data !== 0);
    }

    public async getAndShowLenderMetrics(): Promise<void> {
        this.metrics = await this.lenderDetailsService.getMetricsInfo(this.storageService.getItem(Keys.LENDER_ID));
        this.getNonNpaDelayedLoansCount();
    }

    getNonNpaDelayedLoansCount() {
        if(this.metrics){
            if(this.metrics.numberOfLoansAsNPA && this.metrics.numberOfLoansWithOngoingDelay){
                this.nonNpaDelayedLoansCount = this.metrics.numberOfLoansWithOngoingDelay - this.metrics.numberOfLoansAsNPA;
            } else if(this.metrics.numberOfLoansWithOngoingDelay){
                this.nonNpaDelayedLoansCount = this.metrics.numberOfLoansWithOngoingDelay;
            }
        }
    }

    isPortfolioCreationEnabled(): boolean {
        return this.isPrincipalOutstandingAboutToBeBreached() && !this.areAutomaticInvestmentsFromWalletEnabled();
    }

    public areAutomaticInvestmentsFromWalletEnabled() {
        return this.preferences ? this.preferences.automaticInvestmentsFromWallet : false;
    }

    isFormValid(isValidInputValue: boolean): boolean {
        return (isValidInputValue && this.lender10LakhLimitDeclarationCheck && (this.investmentAmt <= this.walletAmount)
            && (this.investmentAmt >= this.minimumInvestmentLimit) && (this.investmentAmt <= this.maximumInvestmentLimit) && (this.investmentAmt % this.denomination === 0));
    }

    findChartDataForPortfolio(portfolioId: string): ChartData[] {
        return this.portfolioChartData.find(c => c.portfolioId === portfolioId).chartData;
    }

    protected async getRoiForPortfolio(portfolio: any): Promise<number> {
        if (!this.isConsolidatedPortfolioType(portfolio.portfolio_type)) {
            return this.metrics.portfolioMetrics.find(portfolioMetric => portfolioMetric.id === portfolio._id)?.roi;
        } else {
            const lenderId = this.storageService.getItem(Keys.LENDER_ID);
            const metrics = await this.lenderDetailsService.getMetricsForPortfolioType(lenderId, portfolio.portfolio_type);
            return metrics.roi;
        }
    }

    private async getLenderPortfolios(lenderId: string): Promise<any[]> {
        let lenderPortfolios = await this.portfolioService.getLenderPortfoliosInfo(lenderId);
        if (isEmpty(lenderPortfolios)) {
            return [];
        }
        lenderPortfolios = sortBy(lenderPortfolios, portfolio => new Date(portfolio.createdAt)).reverse();
        PortfolioUtil.removeCancelledPortfolios(lenderPortfolios);
        PortfolioUtil.aggregatePortfoliosByType(lenderPortfolios, PortfolioType.Reinvest);
        PortfolioUtil.aggregatePortfoliosByType(lenderPortfolios, PortfolioType.FinzyBolt);
        for (let i = 0; i < lenderPortfolios.length; i++) {
            const portfolioDetail = lenderPortfolios[i];
            portfolioDetail.roi = await this.getRoiForPortfolio(portfolioDetail);
            portfolioDetail.portfolio_created_on = moment(portfolioDetail.createdAt).format('D MMM YYYY ');
        }
        return lenderPortfolios;
    }

    private isPrincipalOutstandingAboutToBeBreached() {
        const minimumInvestmentLimit = ConfigService.getMinimumInvestmentLimit();
        const maximumInvestmentLimit = ConfigService.getMaximumInvestmentLimit();
        return this.metrics ? (this.metrics.principalOutstanding <= maximumInvestmentLimit - minimumInvestmentLimit) : false;
    }

    private updateWalletAmount(lenderDetails): void {
        const minimumWalletBalanceForInitialPortfolio = ConfigService.getMinimumWalletBalanceForInitialPortfolio();
        const walletAmount = lenderDetails?.walletAmount;
        this.walletAmount = Math.round(walletAmount * 100) / 100;
        if (walletAmount < this.minimumInvestmentLimit) {
            this.insufficientWallet = true;
        } else {
            this.insufficientWallet = false;
            this.storageService.saveItem(Keys.WALLET_AMOUNT, walletAmount);
        }
        if (this.lenderPortfolios.length === 0 && walletAmount < minimumWalletBalanceForInitialPortfolio) {
            this.isMinimumWalletBalanceExistForInitialPortfolio = false;
        } else {
            this.isMinimumWalletBalanceExistForInitialPortfolio = true;
        }
    }

    public isConsolidatedPortfolioType(portfolioType: PortfolioType): boolean {
        return portfolioType === PortfolioType.Reinvest || portfolioType === PortfolioType.FinzyBolt;
    }

    public getDelayedLoansRouterLink(): string {
        const lenderId = this.storageService.getItem(Keys.LENDER_ID);

        return `/lender/${lenderId}/delayed-loans`;
    }

    public setShowMoreAndFillFewLenderPortfolios() {
        if (this.lenderPortfolios.length > InvestmentsComponent.minimumNumberOfPortfolioToDisplay) {
            this.isShowMoreRequired = true;
        } else {
            this.isShowMoreRequired = this.isPortfolioCreationEnabled()
                && this.lenderPortfolios.length === InvestmentsComponent.minimumNumberOfPortfolioToDisplay;
        }

        if (this.isShowMoreRequired) {
            const consolidatedPortfolios = this.lenderPortfolios.filter(
                portfolio => [PortfolioType.Reinvest, PortfolioType.FinzyBolt].includes(portfolio.portfolio_type)
            );
            const normalPortfolios = this.lenderPortfolios.filter(
                portfolio => ![PortfolioType.Reinvest, PortfolioType.FinzyBolt].includes(portfolio.portfolio_type)
            );
            const normalPortfoliosRequired = this.isPortfolioCreationEnabled() ?
                InvestmentsComponent.minimumNumberOfPortfolioToDisplay - consolidatedPortfolios.length - 1 :
                InvestmentsComponent.minimumNumberOfPortfolioToDisplay - consolidatedPortfolios.length;
            this.fewLenderPortfolios = [...consolidatedPortfolios, ...normalPortfolios.slice(0, normalPortfoliosRequired)];
        }
    }

    public toggleShowMoreAndShowLess() {
        this.isShowMoreRequired = !this.isShowMoreRequired;
        this.isShowLessRequired = !this.isShowLessRequired;
    }

    private openCreatePortfolioPopup() {
        this.dialog.open(CreatePortfolioComponent, {
            data: {
                principalOutstanding: this.metrics.principalOutstanding,
                numOflenderPortfolios: this.lenderPortfolios? this.lenderPortfolios.length: 0,
                walletAmount: this.walletAmount ?? 0,
            }
        });
    }

    public getFileUrlByFileName(fileName): string{
        return this.configService.getPolicyFileUrlByName(fileName);
    }
}
